import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Col, Button, Form } from "react-bootstrap";

import DefaultModal from "components/modals/ModalTemplate";
import { renderCountdown } from "utils/functions";
import { RESEND_VERIFY_CODE_MESSAGE } from "constant";

interface EnterMFACodeProps {
    onSubmit: (code: string) => void;
    onBack: () => void;
    errorMessage?: string;
    handleLogin: (resend: boolean) => void;
    countdown: number;
    setCountdown: (value: number) => void;
}

/**
 * Renders a confirmation component for OTP input, handling submission,
 * code changes, and countdown timer for resending the code.
 *
 * @param {Object} props - The component properties.
 * @param {function} props.onSubmit - Callback function to handle OTP submission.
 * @param {function} props.onBack - Callback function to handle back navigation.
 * @param {string} props.errorMessage - Error message to display, if any.
 * @param {function} props.handleLogin - Function to handle login logic.
 * @param {number} props.countdown - Countdown timer value for resending OTP.
 * @param {function} props.setCountdown - Function to update the countdown state.
 * @returns {JSX.Element} The rendered component.
 * @throws {Error} Throws an error if the countdown is not a number.
 */
const EnterMFACode = (props: EnterMFACodeProps) => {
    const {
        onSubmit,
        onBack,
        errorMessage,
        handleLogin,
        countdown,
        setCountdown,
    } = props;

    const [code, setCode] = useState("");
    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState("");
    const [modalContent, setModalContent] = useState("");

    const handleSubmitCode = () => {
        onSubmit(code);
    };

    const handleCodeChange = (e: any) => {
        setCode(e.target.value);
    };

    const handleBack = (e: any) => {
        e.preventDefault();
        onBack();
    };

    const handlePressEnter = (e: any) => {
        const isPressEnter = e.key === "Enter";
        if (isPressEnter && code) onSubmit(code);
    };

    const handleResendCode = () => {
        setModalShow(true);
        setModalType("resendConfirm");
        setModalContent(RESEND_VERIFY_CODE_MESSAGE);
    };

    useEffect(() => {
        if (countdown > 0) {
            const timer = setInterval(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => {
                clearInterval(timer);
            };
        }
    }, [countdown, setCountdown]);

    return (
        <>
            <Col lg="6" sm="12" className="login-form">
                <div className="login-box">
                    <div className="login-table">
                        <div className="login-text">
                            <h4>Confirm with OTP</h4>
                        </div>
                        <div className="mt-5 mb-5">
                            Please enter the OTP sent to your mobile number
                        </div>
                        <div>
                            <Form.Group>
                                <Form.Label>OTP</Form.Label>
                                <Form.Control
                                    type="text"
                                    aria-label="verification-code"
                                    onChange={handleCodeChange}
                                    onKeyPress={handlePressEnter}
                                    required
                                />
                            </Form.Group>

                            <Button
                                variant="primary"
                                onClick={handleSubmitCode}
                                disabled={!code}
                            >
                                SUBMIT
                            </Button>
                            <div className="loginf">
                                {errorMessage && (
                                    <p aria-label="errorMessage">
                                        {errorMessage}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-5">
                            Didn't get code?
                            {countdown > 0 ? (
                                <span className="pl-1 green">
                                    {renderCountdown(countdown)}
                                </span>
                            ) : (
                                <Link
                                    to=""
                                    className="link pl-1"
                                    href={"#"}
                                    onClick={handleResendCode}
                                >
                                    Resend code
                                </Link>
                            )}
                        </div>

                        <div className="mt-5">
                            <Link
                                to="/"
                                className="link"
                                href={"#"}
                                onClick={handleBack}
                            >
                                Back to login
                            </Link>
                        </div>
                    </div>
                </div>
            </Col>
            <DefaultModal
                modalShow={modalShow}
                setModalShow={setModalShow}
                modalType={modalType}
                modalContent={modalContent}
                resendFunction={() => {
                    handleLogin(true);
                }}
            />
        </>
    );
};

export default EnterMFACode;